import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { FormattedMessage } from 'dibs-react-intl';
import HeartOutlined from 'dibs-icons/exports/HeartOutlined';
import HeartFilled from 'dibs-icons/exports/HeartFilled';
import { Link } from 'dibs-elements/exports/Link';
import LikeHeartIcon from 'dibs-icons/exports/LikeHeartIcon';
import { Button } from '../../../global/Button/Button';

// mutations
import { SharedSaveSearch } from '../../../mutations/SharedSaveSearch';

// helpers
import classnames from 'classnames';
import { FOLLOW_DEALER_FLOW, sharedLoginCheck } from '../../../authentication/sharedLoginCheck';
import { locations, authLabels } from '../../../utils/tracking/shared/followSearchTracking';
import { getFollowTypeFromUri } from '../../../utils/favoritesUtils';
import { pageTypeConstants as pageTypes } from '../../../constants/pageTypeConstants';

// styles
import styles from './SaveSearchButton.scss';

export class SaveSearchButtonComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            userId: null,
            loading: false,
            waitForConnection: false,
            autoFollow: props.autoFollow,
        };

        this.handleClick = this.handleClick.bind(this);
        this.handleFollowCompleted = this.handleFollowCompleted.bind(this);
        this.handleLoginSuccess = this.handleLoginSuccess.bind(this);
        this.handleLoginError = this.handleLoginError.bind(this);
        this.handleFollow = this.handleFollow.bind(this);
    }

    componentDidUpdate() {
        const { waitForConnection, userId, autoFollow } = this.state;

        /**
         * We must wait for the user fetch before sending off the mutation. We do this by checking
         * whether the favorites connection exists.
         *
         * There's a possibility that the mutation will resolve before the user fetch returns,
         * so the newly returned mutation result will be overridden with whatever comes from the
         * user query.
         */
        if (waitForConnection && userId && this.props.favoritesConnection) {
            this.setState({ waitForConnection: false }, () => {
                this.handleFollow({ userId });
            });
        }

        /**
         * If autoFollow is true, when the component renders it should try and follow the search automatically.
         * We need to consider both situations when the user is and is not logged in.
         *
         * Case 1:
         *
         * User is logged in, so userId is present in props.  If this is the case we need to make sure
         * favoritesConnection is populated (this information is asynced in).  Once we have this information
         * we check to make sure the user is already not following the search.
         * If they are not we trigger the click event on the button to handle the following logic.
         */
        if (autoFollow && this.props.userId && this.props.favoritesConnection) {
            this.setState({ autoFollow: false }, () => {
                if (!this.getFavoriteId()) {
                    this.handleClick();
                }
            });
        }

        /**
         * Case 2:
         *
         * In this case the user is not logged in so we trigger the handleClick function to follow the search.
         * At this point we need to know the user is not logged in, even though we make this check in the handleClick function,
         * because if the user is logged in we need to make sure favoritesConnection is populated (see Case 1)
         *
         */
        if (autoFollow && !this.props.userId) {
            this.setState({ autoFollow: false }, () => {
                this.handleClick();
            });
        }
    }

    handleLoginError() {
        this.setState({ loading: false });
    }

    getFavoriteId(props = this.props) {
        return props.favoritesConnection?.edges?.[0]?.node?.serviceId || props.serviceId;
    }
    getFavoriteRelayId() {
        return this.props.favoritesConnection?.edges?.[0]?.node?.__id;
    }
    handleFollow({ userId, favoritedId }) {
        SharedSaveSearch.commit(this.props.relay.environment, {
            favoriteRelayId: this.getFavoriteRelayId(),
            connectionKey: this.props.connectionKey,
            userId,
            favoritedSearchId: favoritedId,
            page: this.props.page,
            name: this.props.name,
            type: getFollowTypeFromUri(this.props.page),
            onCompleted: this.handleFollowCompleted,
            onError: this.handleLoginError,
        });
    }
    showAuthModal() {
        sharedLoginCheck({
            onSuccess: this.handleLoginSuccess,
            onError: this.handleLoginError,
            authOptions: {
                flow: FOLLOW_DEALER_FLOW,
                ga: { label: authLabels[this.props.location] },
            },
        });
    }
    handleLoginSuccess({ userId }) {
        this.setState({
            waitForConnection: true,
            userId,
        });
    }
    handleClick() {
        this.setState({ loading: true }, () => {
            const { userId } = this.props;
            if (userId) {
                this.handleFollow({
                    userId,
                    favoritedId: this.getFavoriteId(),
                });
            } else {
                this.showAuthModal();
            }
        });
    }
    async handleFollowCompleted(response) {
        const isFollowing =
            response?.favoritePage?.user?.savedSearches?.edges?.[0]?.node?.serviceId;
        await this.props.onFollowSearchCompleted(isFollowing);
        this.setState({ loading: false });
    }

    parsePageType(pageType) {
        if (pageType === pageTypes.MORE_FROM_SELLER || pageType === pageTypes.DEALER) {
            return pageTypes.DEALER;
        } else if (pageType === pageTypes.CREATOR) {
            return pageTypes.CREATOR;
        } else {
            return pageTypes.SEARCH;
        }
    }

    getSaveSearchButtonContent(favoriteId, pageType) {
        if (favoriteId) {
            return (
                <div className={styles.buttonContent}>
                    <HeartFilled className={styles.icon} />
                    <FormattedMessage
                        id="shared.SaveSearchButton.savedSearch"
                        defaultMessage="{pageType, select,
                        seller {Saved Seller}
                        creator {Saved Creator}
                        other {Saved Search}
                    }"
                        values={{ pageType }}
                    />
                </div>
            );
        } else {
            const className = classnames(styles.icon, styles.button);

            return (
                <div className={styles.buttonContent}>
                    <HeartOutlined className={className} />
                    <FormattedMessage
                        id="shared.SaveSearchButton.saveSearch"
                        defaultMessage="{pageType, select,
                        seller {Save Seller}
                        creator {Save Creator}
                        other {Save Search}
                    }"
                        values={{ pageType }}
                    />
                </div>
            );
        }
    }

    render() {
        const { size, fullWidth, type, itemSearch } = this.props;
        const { pageType } = itemSearch || {};

        const parsedPageType = this.parsePageType(pageType);
        const favoriteId = this.getFavoriteId();

        const className = classnames(styles.button, this.props.className);

        return type === 'heart' ? (
            <Link
                className={styles.heartLink}
                onClick={this.handleClick}
                dataTn="mobile-item-actions-follow-search"
            >
                <LikeHeartIcon
                    className={styles.heartIcon}
                    isFilled={favoriteId || this.state.loading}
                    animateHeartBeat={this.state.loading}
                    theme="dark"
                />
            </Link>
        ) : (
            <Button
                className={className}
                disabled={this.state.loading}
                size={size}
                isSecondary={size !== 'large' && type !== 'primary'}
                fullWidth={fullWidth}
                onClick={this.handleClick}
                dataTn="item-actions-follow-search"
            >
                {this.getSaveSearchButtonContent(favoriteId, parsedPageType)}
            </Button>
        );
    }
}

SaveSearchButtonComponent.propTypes = {
    className: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    fullWidth: PropTypes.bool,
    userId: PropTypes.string,
    user: PropTypes.object,
    relay: PropTypes.object,
    page: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onFollowSearchCompleted: PropTypes.func,
    favoritesConnection: PropTypes.object,
    autoFollow: PropTypes.bool,
    connectionKey: PropTypes.string,
    location: PropTypes.oneOf(Object.values(locations)),
    type: PropTypes.oneOf(['primary', 'secondary', 'heart']),
    serviceId: PropTypes.string,
    itemSearch: PropTypes.object,
};

SaveSearchButtonComponent.defaultProps = {
    size: 'large',
    fullWidth: false,
    autoFollow: false,
    favoritesConnection: PropTypes.object,
    onFollowSearchCompleted: () => ({}),
};

export const SaveSearchButton = createFragmentContainer(SaveSearchButtonComponent, {
    itemSearch: graphql`
        fragment SaveSearchButton_itemSearch on ItemSearchQueryConnection {
            pageType
        }
    `,
    favoritesConnection: graphql`
        fragment SaveSearchButton_favoritesConnection on favoritesConnection {
            edges {
                node {
                    __id
                    serviceId
                }
            }
        }
    `,
});
